import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import ReactGoogleMapLoader from 'react-google-maps-loader';
import ReactGooglePlacesSuggest from 'react-google-places-suggest';
import getCountryIso3 from 'country-iso-2-to-3';
import getCoutryIso2 from 'country-iso-3-to-2';

import { useDispatch, useSelector } from 'react-redux';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import {
  findStateLabelByValue,
  findCountryByCode,
  findCountryLabelByValue,
  findStateValueByLabel,
} from '../../util/helpers/countries';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import useCountries from '../../hooks/useCountries';
import { getStates } from '../../store/actions/common/commonActions';
import { selectStates } from '../../store/selectors/commonSelectors';
import useApplicationUid from '../../hooks/useApplicationUid';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';

import {
  APPLICANT_TYPES,
  APPLICATION_TYPE,
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CorporateAccountType,
  USA_COUNTRY_LABEL,
} from '../../util/constants';
import useStepPost from '../../hooks/useStepPost';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  BASIC_ACCOUNT_QUERY_DATA,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  SAME_ADDRESS,
} from '../../constants/sessionStorage';
import { getApplication } from '../../store/actions/application/applicationActions';
import useLegalEntityAddressDispatch from '../../hooks/useLegalEntityAddressDispatch';
import usePersonAddressDispatch from '../../hooks/usePersonAddressDispatch';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import Select from '../Fields/Select';
import {
  iraCountryOfCitizenshipButtonDisable,
  iraCountryOfCitizenshipNote,
} from '../../util/helpers/iraAccountHelpers';
import {
  utmaCountryOfCitizenshipButtonDisable,
  utmaCountryOfCitizenshipNote,
} from '../../util/helpers/utmaAccountHelper';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { getBasicAccount } from '../../store/actions/user/userActions';
import { selectBasicAccount } from '../../store/selectors/userSelectors';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import {
  APPLICATION_TYPE_IRA,
  APPLICATION_TYPE_UTMA,
} from '../../constants/accountTypes';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { removeControlCharacters } from '../../util/helpers/stringHelpers';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const SuggestItemWrapper = styled.div`
  padding: 15px;
`;

const HiddenInput = styled.input`
  opacity: 0;
  cursor: 'context-menu';
  width: 0;
  height: 0;
`;

const AddressForm = ({
  nextPath,
  applicantType,
  hasSameAddress,
  onModalClose,
  applicationType,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData(applicantType);
  const ApplicantDataMain = useApplicantData(APPLICANT_TYPES.MAIN);
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const [address, setAddress] = useState({});
  const [countryCode, setCountryCode] = useState('');
  const { legalEntityAddressDispatch } = useLegalEntityAddressDispatch();
  const { personAddressDispatch } = usePersonAddressDispatch();
  const { BrokerCode } = useSelector(selectSettings);
  const [countryValue, setCountryValue] = useState('');
  const [stateValue, setStateValue] = useState('');
  const ApplicantDataValues = hasSameAddress
    ? ApplicantDataMain
    : ApplicantData;

  const countries = useCountries();
  const states = useSelector(selectStates);
  const basicAccountData = useSelector(selectBasicAccount);
  const Application = useSelector(selectApplication);
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;

  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const defaultAddress = basicAccountData?.Addresses?.find(
    (element) => element.IsDefaultMailingAddress === true,
  );
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const ApplicantDataKey =
    applicantType === APPLICANT_TYPES.BUSINESS
      ? 'AddressResponse'
      : 'PersonAddress';

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (applicantType === APPLICANT_TYPES.BUSINESS) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Business Address',
        accountType: accountGtmType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Address',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
      });
      return;
    }

    if (applicantType === APPLICANT_TYPES.BUSINESS) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Business Address',
        accountType: accountGtmType,
      });
      return;
    }

    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Address Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
    });
  };

  useEffect(() => {
    const { ApplicationType } = Application;
    if (
      isUpgrade &&
      basicAccountQueryData?.accountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: basicAccountQueryData?.accountUid,
        }),
      );
      return;
    }
    if (
      isUpgrade &&
      Application?.AccountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: Application?.AccountUid,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: [
        'Street',
        'AptOrSuite',
        'State',
        'City',
        'PostalCode',
        'Country',
      ],
      countryCode,
    }),
  });

  const formValidationSchemaImpersonate = Yup.object().shape(
    {
      ...getValidationSchemaImpersonate({
        fields: [
          'Street',
          'AptOrSuite',
          'State',
          'City',
          'PostalCode',
          'Country',
        ],
        countryCode,
      }),
    },
    [
      ['Street', 'Street'],
      ['City', 'City'],
      ['PostalCode', 'PostalCode'],
      ['Country', 'State'],
    ],
  ); // cyclic dependency);

  const COUNTRY_OPTIONS = [
    ...countries.map((country) => ({
      value: country.Iso3Code,
      label: country.Caption,
      dataCy: 'menu-item-country',
    })),
  ];
  const STATE_OPTIONS = [
    ...states.map((state) => ({
      value: state.Code,
      label: state.Caption,
      dataCy: 'menu-item-state',
    })),
  ];
  const { handleSubmit, control, setValue, setError, watch } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
  });

  // useEffect(() => {
  //   console.log(1);
  //   updateFormValues(address);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [basicAccountData]);

  const countryField = watch('Country');
  const stateField = watch('State');
  const cityField = watch('City');

  const setFormValue = (label, value) => {
    if (value) {
      setValue(label, value, { shouldValidate: true });
    }
  };

  const updateFormValues = (address) => {
    if (address) {
      const {
        CountryIso3Code,
        Street,
        AptOrSuite,
        City,
        PostalCode,
        State,
      } = address;
      setFormValue('Country', {
        label: CountryIso3Code
          ? findCountryByCode(countries, CountryIso3Code)
          : USA_COUNTRY_LABEL,
        value: CountryIso3Code || 'USA',
      });

      setFormValue('Street', Street);

      setFormValue('AptOrSuite', AptOrSuite);
      if (!City && !cityField) {
        setValue('City', '');
      } else {
        setFormValue('City', City, { shouldDirty: true });
      }
      if (!PostalCode) {
        setValue('PostalCode', '', { shouldDirty: true });
      } else {
        setFormValue('PostalCode', PostalCode);
      }

      if (!State?.value) {
        setValue('State', '', { shouldDirty: true });
      }

      if (State) {
        if (shouldFetchStatesObject(CountryIso3Code)) {
          setFormValue('State', {
            value: State,
            label: findStateLabelByValue(states, State),
          });
        } else {
          setFormValue('State', State);
        }
      }
    }
  };

  const shouldFetchStatesObject = useCallback((CountryIso3Code) => {
    if (CountryIso3Code !== 'USA' && CountryIso3Code !== 'CAN') {
      return false;
    }

    return true;
  }, []);

  const isBusinessType =
    APPLICATION_TYPE[applicationType] === APPLICATION_TYPE[2] &&
    [
      CorporateAccountType[1],
      CorporateAccountType[2],
      CorporateAccountType[3],
      CorporateAccountType[5],
    ].includes(AccountType);

  useEffect(() => {
    if (countryCode === 'USA' || countryCode === 'CAN') {
      dispatch(getStates({ countryCode }));
    }
  }, [countryCode, dispatch]);

  useEffect(() => {
    if (stateField && stateField?.value) {
      setFormValue('State', {
        value: stateField?.value,
        label: findStateLabelByValue(states, stateField?.value),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  useEffect(() => {
    updateFormValues(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    const addressResponse = getApplicantData(
      ApplicantDataValues,
      ApplicantDataKey,
    );
    updateFormValues(addressResponse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantDataValues, ApplicantDataKey]);

  useEffect(() => {
    if (countryField) {
      setCountryCode(countryField.value);
    }
  }, [countryField]);

  useEffect(() => {
    if (countryValue) {
      setCountryCode(countryValue);
    }
  }, [countryValue]);

  useEffect(() => {
    setFormValue('State', '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => {
    if (
      ApplicantData?.PersonCountry &&
      ApplicantData?.PersonCountry?.CountryIso3Code &&
      !ApplicantData?.PersonAddress?.CountryIso3Code
    ) {
      setValue('Country', {
        value: ApplicantData?.PersonCountry?.CountryIso3Code,
        label: findCountryLabelByValue(
          countries,
          ApplicantData?.PersonCountry?.CountryIso3Code,
        ),
      });
      setCountryValue(ApplicantData?.PersonCountry?.CountryIso3Code);
    }
    if (
      defaultAddress &&
      defaultAddress?.CountryIso3Code &&
      applicantType === APPLICANT_TYPES.BUSINESS
    ) {
      setValue('Country', {
        value: defaultAddress?.CountryIso3Code,
        label: findCountryLabelByValue(
          countries,
          defaultAddress?.CountryIso3Code,
        ),
      });
      setCountryValue(defaultAddress?.CountryIso3Code);
    }
    if (
      defaultAddress &&
      defaultAddress?.CountryIso3Code &&
      applicantType === APPLICANT_TYPES.MAIN &&
      !isBusinessType
    ) {
      setValue('Country', {
        value: defaultAddress?.CountryIso3Code,
        label: findCountryLabelByValue(
          countries,
          defaultAddress?.CountryIso3Code,
        ),
      });
      setCountryValue(defaultAddress?.CountryIso3Code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApplicantData, countries, applicantType, isBusinessType]);

  useEffect(() => {
    if (applicantType === APPLICANT_TYPES.BUSINESS && defaultAddress) {
      setAddress(defaultAddress);
    }
    if (
      defaultAddress &&
      applicantType === APPLICANT_TYPES.MAIN &&
      !isBusinessType
    ) {
      setAddress(defaultAddress);
    }
  }, [defaultAddress, applicantType, isBusinessType]);

  const onAPISuccess = () => {
    dispatch(getApplication({ data: { ApplicationUid } }));
    onSuccess();
  };

  const onSubmit = ({
    Street,
    AptOrSuite,
    City,
    State,
    Country,
    PostalCode,
  }) => {
    const addressResponse = getApplicantData(
      ApplicantDataValues,
      ApplicantDataKey,
    );
    const defaultValues = () => {
      if (
        [APPLICANT_TYPES.MAIN, APPLICANT_TYPES.BUSINESS].includes(applicantType)
      ) {
        return addressResponse || defaultAddress;
      }
      if (
        applicantType === APPLICANT_TYPES.SECONDARY ||
        applicantType === APPLICANT_TYPES.MINOR
      ) {
        return ApplicantData.PersonAddress;
      }
      return addressResponse;
    };

    const getPersonUid = () => {
      if (
        applicantType === APPLICANT_TYPES.SECONDARY ||
        applicantType === APPLICANT_TYPES.MINOR
      ) {
        return ApplicantData?.PersonUid;
      }
      return ApplicantDataValues?.PersonUid;
    };

    const submitData = {
      personUid: getPersonUid(),
      defaultValues: {
        ...defaultValues(),
      },
      data: {
        Street: removeControlCharacters(Street) || null,
        AptOrSuite: removeControlCharacters(AptOrSuite) || null,
        City: removeControlCharacters(City) || null,
        State:
          Country?.value === 'USA' || Country?.value === 'CAN'
            ? State.value || null
            : State || null,
        PostalCode: removeControlCharacters(PostalCode) || null,
        CountryIso3Code: Country?.value,
        CountryIso2Code: getCoutryIso2(Country?.value),
      },
      onSuccess:
        eventType === 'Form'
          ? () => onAPISuccess()
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? setError
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    };

    if (hasSameAddress) {
      removeFromSessionStorage(SAME_ADDRESS);
    }

    if (applicantType === APPLICANT_TYPES.BUSINESS) {
      legalEntityAddressDispatch({
        ...submitData,
      });
      return;
    }

    personAddressDispatch({
      ...submitData,
    });
  };

  const processAddress = (addressDetails) => {
    const addressDetailTypeMap = {
      subpremise: 'AptOrSuite',
      street_number: 'Street',
      premise: 'AptOrSuite',
      route: 'Street',
      locality: 'City',
      postal_town: 'City',
      administrative_area_level_3: 'City',
      administrative_area_level_1: 'State',
      country: 'CountryIso3Code',
      postal_code: 'PostalCode',
    };

    const addressMap = {
      Street: {
        prop: 'short_name',
        values: [],
      },
      AptOrSuite: {
        prop: 'short_name',
        values: [],
      },
      City: {
        prop: 'long_name',
        values: [],
      },
      State: {
        prop: 'short_name',
        values: [],
      },
      CountryIso3Code: {
        prop: 'short_name',
        values: [],
        process: (value) => getCountryIso3(value),
      },
      PostalCode: {
        prop: 'short_name',
        values: [],
      },
    };

    addressDetails.forEach((detail) => {
      detail.types.forEach((type) => {
        if (addressDetailTypeMap[type]) {
          const { prop, process, values } = addressMap[
            addressDetailTypeMap[type]
          ];
          values.push(process ? process(detail[prop]) : detail[prop]);
        }
      });
    });
    setAddress(
      Object.fromEntries(
        Object.entries(addressMap).map(([key, { values }]) => [
          key,
          values.join(' '),
        ]),
      ),
    );
  };

  const handleSelectSuggest = (geocodedPrediction) => {
    setSearch('');
    processAddress(geocodedPrediction.address_components);
  };

  useEffect(() => {
    const { State } = address;
    if (State) {
      if (countryCode === 'USA' || countryCode === 'CAN') {
        setFormValue('State', {
          value: State,
          label: findStateLabelByValue(states, State),
        });
      } else {
        setFormValue('State', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, states]);

  const handleChangeCountry = (event) => {
    setValue('State', '');
    setCountryValue(getCountryIso3(event.target.value));
  };

  const handleChangeState = (event) => {
    setStateValue(event.target.value);
  };

  // useEffect(() => {
  //   if (countryValue) {
  //     setValue('Country', {
  //       label: findCountryLabelByValue(countries, countryValue),
  //       value: countryValue,
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [countryValue]);

  useEffect(() => {
    if (stateValue.length === 2) {
      if (countryCode === 'USA' || countryCode === 'CAN') {
        setFormValue('State', {
          label: findStateLabelByValue(states, stateValue),
          value: stateValue,
        });
      } else {
        setFormValue('State', stateValue);
      }
    }

    if (stateValue.length > 2) {
      if (countryCode === 'USA' || countryCode === 'CAN') {
        setFormValue('State', {
          label: stateValue,
          value: findStateValueByLabel(states, stateValue),
        });
      } else {
        setFormValue('State', stateValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValue, countryCode, states]);

  useEffect(() => {
    if (hasSameAddress) {
      updateFormValues(ApplicantDataMain?.PersonAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSameAddress, ApplicantDataMain]);

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <HiddenInput
          type="text"
          autoComplete="country"
          value={countryValue}
          onChange={handleChangeCountry}
        />
        {/* <Controller
          control={control}
          render={({ field: { ref, onChange, ...field }, fieldState }) => (
            <Select
              label={t('country')}
              options={COUNTRY_OPTIONS}
              {...field}
              {...fieldState}
              onChange={(...args) => {
                onChange(...args);
                // setStateValue('');
                // setValue('State', '');
                // setValue('AptOrSuite', '');
                // setValue('Street', '');
                // setValue('City', '');
                // setValue('PostalCode', '');
              }}
            />
          )}
          name="Country"
        /> */}

        <Controller
          control={control}
          render={({ field: { ref, ...field }, fieldState }) => {
            setCountryCode(field?.value?.value);
            return (
              <Select
                options={COUNTRY_OPTIONS}
                {...field}
                {...fieldState}
                autoFocus
                onChange={(...args) => {
                  setValue('Country', {
                    value: args[0].value,
                    label: args[0].label,
                  });
                  setStateValue('');
                  setValue('State', '');
                  setValue('AptOrSuite', '');
                  setValue('Street', '');
                  setValue('City', '');
                  setValue('PostalCode', '');
                }}
                dataCyButtonToggle="button-toggle-country-list"
              />
            );
          }}
          name="Country"
        />
        {iraCountryOfCitizenshipNote(countryCode, BrokerCode)}
        {utmaCountryOfCitizenshipNote(countryCode, BrokerCode)}

        <ReactGoogleMapLoader
          params={{
            key: process.env.REACT_APP_MAPS_API_KEY,
            libraries: 'places',
          }}
          render={(googleMaps) =>
            googleMaps && (
              <ReactGooglePlacesSuggest
                googleMaps={googleMaps}
                displayPoweredByGoogle={false}
                autocompletionRequest={{
                  input: search,
                }}
                onSelectSuggest={handleSelectSuggest}
                textNoResults={t('secureAddressEnter.notFound')}
                customRender={(prediction) => (
                  <SuggestItemWrapper>
                    {prediction
                      ? prediction.description
                      : t('secureAddressEnter.notFound')}
                  </SuggestItemWrapper>
                )}
              >
                <Controller
                  control={control}
                  render={({ field: { onChange, ...field }, fieldState }) => (
                    <InputText
                      {...field}
                      {...fieldState}
                      onChange={(e) => {
                        onChange(e);
                        setSearch(e.target.value);
                      }}
                      placeholder="Street Address"
                      autocomplete="address-line1"
                      label={t('secureAddressEnter.street')}
                      data-cy="input-street-address"
                    />
                  )}
                  name="Street"
                />
              </ReactGooglePlacesSuggest>
            )
          }
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              {...field}
              {...fieldState}
              label={t('secureAddressEnter.aptOrSuite')}
              autocomplete="address-line2"
              data-cy="input-apt"
            />
          )}
          name="AptOrSuite"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              {...field}
              {...fieldState}
              label={t('secureAddressEnter.city')}
              data-cy="input-city"
            />
          )}
          name="City"
        />
      </FormContainer>
      <FormContainer>
        <HiddenInput
          type="text"
          autoComplete="address-level1"
          value={stateValue}
          onChange={handleChangeState}
        />
        {countryCode === 'USA' || countryCode === 'CAN' ? (
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t('secureAddressEnter.state')}
                options={STATE_OPTIONS}
                isSearchable={false}
                {...field}
                {...fieldState}
                dataCyButtonToggle="button-toggle-state"
              />
            )}
            name="State"
          />
        ) : (
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                {...fieldState}
                label={t('secureAddressEnter.state')}
                data-cy="ipnut-state"
              />
            )}
            name="State"
          />
        )}
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              {...field}
              {...fieldState}
              label={t('secureAddressEnter.postalCode')}
              data-cy="input-postal-code"
            />
          )}
          name="PostalCode"
          autocomplete="postal-code"
        />
      </FormContainer>
      <OnBoardingCta
        type="submit"
        onClick={() => {
          setEventType('Form');
          handleGtmEvent();
        }}
        disabled={
          iraCountryOfCitizenshipButtonDisable(countryCode) ||
          utmaCountryOfCitizenshipButtonDisable(countryCode)
        }
        title={nextPath ? t('continue') : t('update')}
        buttonProps={{
          'data-cy': nextPath ? 'button-continue' : 'button-update',
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={() => onAPISuccess()}
        />
      )}
    </FormWrap>
  );
};

AddressForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  hasSameAddress: PropTypes.bool,
  applicationType: PropTypes.number,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default AddressForm;
